<template>
  <section id="shop" class="shop-section">
    <div class="shop-heading">
      <h2>Shop</h2>
      <button class="cart-button" @click="cartOpen = true">
        Cart ({{ cartQuantity }})
      </button>
    </div>

    <p v-if="checkoutComplete" class="checkout-success">
      Thank you. Your Square checkout was completed successfully.
    </p>

    <p class="shop-subtitle">
      Discover our current 3D prints, ready to personalise and add to your cart.
      <span v-if="productsUpdatedAt" class="shop-updated">Updated: {{ productsUpdatedAt }}</span>
    </p>

    <div v-if="productsError" class="shop-error">
      {{ productsError }}
    </div>

    <div v-else-if="productsLoading && !hasLoaded" class="shop-loading">
      Loading products…
    </div>

    <template v-else>
      <div class="shop-controls">
        <label class="shop-search">
          <span class="sr-only">Search shop products</span>
          <input
              v-model="searchQuery"
              type="search"
              placeholder="Search products"
              aria-label="Search products"
              @input="scheduleLoad"
          />

        </label>

        <label class="page-size-control">
          <span>Products per page</span>
          <select v-model.number="pageSize" @change="changePageSize">
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>
        </label>
      </div>

      <div class="shop-groups" aria-label="Product categories">
        <button
            class="filter-button"
            :class="{ active: selectedCategory === '' }"
            @click="selectCategory('')"
        >
          All
        </button>
        <button
            v-for="category in categories"
            :key="category"
            class="filter-button"
            :class="{ active: selectedCategory === category }"
            @click="selectCategory(category)"
        >
          {{ category }}
        </button>
      </div>

      <p class="shop-results">
        {{ pagination.total }} {{ pagination.total === 1 ? "product" : "products" }}
      </p>

      <p v-if="productsLoading" class="shop-loading">Updating products…</p>

      <div v-else-if="products.length" class="shop-grid">
        <div v-for="p in products" :key="p.id" class="shop-card">
          <router-link
              :to="{ name: 'product', params: { id: p.id } }"
              class="shop-image-wrap"
              :aria-label="`View ${p.title}`"
          >
            <img
                v-if="p.image"
                :src="displayImageUrl(p.image)"
                :alt="p.title"
                class="shop-image"
                loading="lazy"
            />
            <div v-else class="shop-image-placeholder">
              <img
                  :src="displayImageUrl('https://res.cloudinary.com/dcespjfhu/image/upload/v1780898741/bkzqdkzc6juvkqz5nd0y.avif')"
                  alt=""
                  class="shop-image shop-fallback-image"
                  loading="lazy"
                  @error="$event.currentTarget.hidden = true"
              />
              <span>Photos coming soon</span>
            </div>
            <img
                :src="`./logos/${determineLogo(p.brand)}`"
                alt=""
                class="shop-logo-overlay"
            />
          </router-link>

          <div class="shop-card-body">
            <router-link
                :to="{ name: 'product', params: { id: p.id } }"
                class="shop-title"
            >
              {{ p.title }}
            </router-link>
            <div class="shop-category">{{ p.category }}</div>

            <div v-if="p.modifierGroups?.length" class="product-options">
              <div v-for="group in p.modifierGroups" :key="group.id" class="product-option-group">
                <label class="product-option-label">{{ group.name }}</label>

                <select
                    v-if="group.selectionType === 'SINGLE'"
                    :value="selectedModifierIds(p.id, group.id)[0] || ''"
                    @change="setSingleModifier(p.id, group.id, $event.target.value)"
                >
                  <option v-if="group.minSelected === 0" value="">No option</option>
                  <option v-for="modifier in group.modifiers" :key="modifier.id" :value="modifier.id">
                    {{ modifier.name }}{{ modifier.price ? ` (+${formatPrice(modifier.price, modifier.currency)})` : "" }}
                  </option>
                </select>

                <label
                    v-for="modifier in group.modifiers"
                    v-else
                    :key="modifier.id"
                    class="modifier-checkbox"
                >
                  <input
                      type="checkbox"
                      :checked="selectedModifierIds(p.id, group.id).includes(modifier.id)"
                      @change="toggleModifier(p.id, group, modifier.id)"
                  />
                  <span>
                    {{ modifier.name }}{{ modifier.price ? ` (+${formatPrice(modifier.price, modifier.currency)})` : "" }}
                  </span>
                </label>
              </div>
            </div>

            <p v-if="modifierErrors[p.id]" class="product-option-error">
              {{ modifierErrors[p.id] }}
            </p>

            <div class="shop-row">
              <div class="shop-price" v-if="p.price !== null && p.price !== undefined">
                {{ formatPrice(p.price, p.currency) }}
              </div>

              <button
                  class="add-to-cart-button"
                  :disabled="!p.available || p.price === null"
                  @click="addToCart(p)"
              >
                {{ !p.available ? "Sold out" : p.price === null ? "Unavailable" : "Add to cart" }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <p v-else class="shop-empty">No products found for this search and category.</p>

      <nav v-if="pagination.totalPages > 1" class="shop-pagination" aria-label="Shop pagination">
        <button :disabled="pagination.page <= 1" @click="changePage(pagination.page - 1)">
          Previous
        </button>
        <span>Page {{ pagination.page }} of {{ pagination.totalPages }}</span>
        <button :disabled="pagination.page >= pagination.totalPages" @click="changePage(pagination.page + 1)">
          Next
        </button>
      </nav>
    </template>
  </section>

  <div v-if="cartOpen" class="cart-backdrop" @click.self="cartOpen = false">
    <aside class="cart-panel" role="dialog" aria-modal="true" aria-labelledby="cart-title">
      <div class="cart-header">
        <h2 id="cart-title">Your cart</h2>
        <button class="cart-close" aria-label="Close cart" @click="cartOpen = false">×</button>
      </div>

      <p v-if="cart.length === 0" class="cart-empty">Your cart is empty.</p>

      <div v-else class="cart-lines">
        <article v-for="line in cart" :key="line.key" class="cart-line">
          <img v-if="line.image" :src="displayImageUrl(line.image, 120, 120)" :alt="line.title" />
          <div class="cart-line-details">
            <strong>{{ line.title }}</strong>
            <span v-for="modifier in line.modifiers" :key="modifier.id">
              {{ modifier.name }}
            </span>
            <span>{{ formatPrice(lineUnitPrice(line), line.currency) }} each</span>
          </div>

          <div class="cart-quantity">
            <button aria-label="Reduce quantity" @click="changeCartQuantity(line.key, -1)">−</button>
            <span>{{ line.quantity }}</span>
            <button aria-label="Increase quantity" @click="changeCartQuantity(line.key, 1)">+</button>
          </div>

          <button class="cart-remove" @click="removeCartLine(line.key)">Remove</button>
        </article>
      </div>

      <div v-if="cart.length" class="cart-footer">
        <div class="shipping-fields">
          <label>
            <span>Shipping country</span>
            <select v-model="shippingCountry" @change="checkoutError = ''">
              <option value="">Choose a country</option>
              <option v-for="country in shippingCountries" :key="country.code" :value="country.code">
                {{ country.name }}
              </option>
            </select>
          </label>

          <label v-if="shippingCountry === 'GB'">
            <span>Postcode</span>
            <input
                v-model.trim="shippingPostcode"
                type="text"
                autocomplete="postal-code"
                placeholder="TS1 1AA"
                @input="checkoutError = ''"
            />
          </label>
        </div>

        <p v-if="shippingCountry" class="shipping-summary">{{ shippingSummary }}</p>
        <div class="cart-total">
          <span>Estimated total</span>
          <strong>{{ formatPrice(cartTotal + shippingAmount, cartCurrency) }}</strong>
        </div>
        <p class="cart-note">Final pricing, taxes, discounts, and delivery details are confirmed securely by Square.</p>
        <p v-if="checkoutError" class="cart-error">{{ checkoutError }}</p>
        <button class="checkout-button" :disabled="checkoutLoading" @click="checkout">
          {{ checkoutLoading ? "Opening Square checkout…" : "Checkout securely with Square" }}
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import { centerCropCloudinaryUrl } from "../utils/cloudinary.js";

export default {
  data() {
    return {
      products: [],
      productsLoading: true,
      hasLoaded: false,
      productsError: "",
      productsUpdatedAt: "",
      categories: [],
      selectedCategory: "",
      searchQuery: "",
      pageSize: 20,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 1,
      },
      searchTimer: null,
      cart: [],
      cartOpen: false,
      checkoutLoading: false,
      checkoutError: "",
      checkoutComplete: false,
      shippingCountry: "GB",
      shippingPostcode: "",
      shippingCountries: [
        {code: "GB", name: "United Kingdom"},
        {code: "AL", name: "Albania"}, {code: "AD", name: "Andorra"},
        {code: "AT", name: "Austria"}, {code: "BY", name: "Belarus"},
        {code: "BE", name: "Belgium"}, {code: "BA", name: "Bosnia and Herzegovina"},
        {code: "BG", name: "Bulgaria"}, {code: "HR", name: "Croatia"},
        {code: "CY", name: "Cyprus"}, {code: "CZ", name: "Czechia"},
        {code: "DK", name: "Denmark"}, {code: "EE", name: "Estonia"},
        {code: "FI", name: "Finland"}, {code: "FR", name: "France"},
        {code: "DE", name: "Germany"}, {code: "GR", name: "Greece"},
        {code: "HU", name: "Hungary"}, {code: "IS", name: "Iceland"},
        {code: "IE", name: "Ireland"}, {code: "IT", name: "Italy"},
        {code: "LV", name: "Latvia"}, {code: "LI", name: "Liechtenstein"},
        {code: "LT", name: "Lithuania"}, {code: "LU", name: "Luxembourg"},
        {code: "MT", name: "Malta"}, {code: "MD", name: "Moldova"},
        {code: "MC", name: "Monaco"}, {code: "ME", name: "Montenegro"},
        {code: "NL", name: "Netherlands"}, {code: "MK", name: "North Macedonia"},
        {code: "NO", name: "Norway"}, {code: "PL", name: "Poland"},
        {code: "PT", name: "Portugal"}, {code: "RO", name: "Romania"},
        {code: "SM", name: "San Marino"}, {code: "RS", name: "Serbia"},
        {code: "SK", name: "Slovakia"}, {code: "SI", name: "Slovenia"},
        {code: "ES", name: "Spain"}, {code: "SE", name: "Sweden"},
        {code: "CH", name: "Switzerland"}, {code: "UA", name: "Ukraine"},
        {code: "VA", name: "Vatican City"},
      ],
      modifierSelections: {},
      modifierErrors: {},
    }
  },

  mounted() {
    this.loadCart();
    if (this.$route.query.cart === "open") {
      this.cartOpen = true;
    }
    if (this.$route.query.checkout === "success") {
      this.checkoutComplete = true;
      this.cart = [];
      this.saveCart();
    }
    this.loadProducts();
  },

  beforeUnmount() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  },

  computed: {
    cartQuantity() {
      return this.cart.reduce((total, line) => total + line.quantity, 0);
    },

    cartCurrency() {
      return this.cart[0]?.currency || "GBP";
    },

    cartTotal() {
      return this.cart.reduce((total, line) => total + (this.lineUnitPrice(line) * line.quantity), 0);
    },

    shippingAmount() {
      if (this.shippingCountry === "GB") {
        if (!this.validUkShippingPostcode) {
          return 0;
        }
        return this.normalizedShippingPostcode.startsWith("TS") ? 0 : 3.95;
      }
      return this.shippingCountry ? 6 : 0;
    },

    shippingSummary() {
      if (this.shippingCountry === "GB" && !this.shippingPostcode) {
        return "Enter your postcode to calculate delivery";
      }
      if (this.shippingCountry === "GB" && !this.validUkShippingPostcode) {
        return "Enter a valid UK postcode";
      }
      if (this.shippingCountry === "GB" && this.normalizedShippingPostcode.startsWith("TS")) {
        return "Teesside delivery: free";
      }
      if (this.shippingCountry === "GB") {
        return "UK delivery: £3.95";
      }
      return "Europe delivery: £6.00";
    },

    normalizedShippingPostcode() {
      return this.shippingPostcode.replace(/\s+/g, "").toUpperCase();
    },

    validUkShippingPostcode() {
      return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(this.shippingPostcode.trim());
    },
  },

  methods: {
    displayImageUrl(url, width = 640, height = 640) {
      return centerCropCloudinaryUrl(url, { width, height });
    },

    async loadProducts(page = 1) {
      this.productsLoading = true;
      this.productsError = "";

      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(this.pageSize),
        });
        if (this.searchQuery.trim()) {
          params.set("search", this.searchQuery.trim());
        }
        if (this.selectedCategory) {
          params.set("category", this.selectedCategory);
        }

        const res = await fetch(`/api/products?${params.toString()}`, { cache: "no-store" });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "Couldn’t load Square products.");
        }

        const data = await res.json();
        this.products = Array.isArray(data.products) ? data.products : [];
        this.initialiseModifierSelections(this.products);
        this.categories = Array.isArray(data.categories) ? data.categories : [];
        this.pagination = data.pagination || {
          page: 1,
          pageSize: this.pageSize,
          total: this.products.length,
          totalPages: 1,
        };
        this.productsUpdatedAt = data.updatedAt ? new Date(data.updatedAt).toLocaleString() : "";
      } catch (err) {
        console.error("Error loading Square products:", err);
        this.productsError = (err && err.message) ? err.message : "Failed to load Square products.";
      } finally {
        this.productsLoading = false;
        this.hasLoaded = true;
      }
    },

    scheduleLoad() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadProducts(1), 300);
    },

    selectCategory(category) {
      this.selectedCategory = category;
      this.loadProducts(1);
    },

    changePageSize() {
      this.loadProducts(1);
    },

    changePage(page) {
      this.loadProducts(page);
      window.scrollTo({top: 0, behavior: "smooth"});
    },

    initialiseModifierSelections(products) {
      for (const product of products) {
        if (!this.modifierSelections[product.id]) {
          this.modifierSelections[product.id] = {};
        }

        for (const group of product.modifierGroups || []) {
          if (!this.modifierSelections[product.id][group.id]) {
            this.modifierSelections[product.id][group.id] = group.modifiers
                .filter((modifier) => modifier.onByDefault)
                .map((modifier) => modifier.id);
          }
        }
      }
    },

    selectedModifierIds(productId, groupId) {
      return this.modifierSelections[productId]?.[groupId] || [];
    },

    setSingleModifier(productId, groupId, modifierId) {
      this.modifierSelections[productId][groupId] = modifierId ? [modifierId] : [];
      this.modifierErrors[productId] = "";
    },

    toggleModifier(productId, group, modifierId) {
      const selected = [...this.selectedModifierIds(productId, group.id)];
      const index = selected.indexOf(modifierId);
      if (index >= 0) {
        selected.splice(index, 1);
      } else if (!group.maxSelected || selected.length < group.maxSelected) {
        selected.push(modifierId);
      }
      this.modifierSelections[productId][group.id] = selected;
      this.modifierErrors[productId] = "";
    },

    addToCart(product) {
      const selectedModifiers = [];
      for (const group of product.modifierGroups || []) {
        const selectedIds = this.selectedModifierIds(product.id, group.id);
        if (selectedIds.length < group.minSelected) {
          this.modifierErrors[product.id] = `Choose at least ${group.minSelected} option from ${group.name}.`;
          return;
        }
        if (group.maxSelected && selectedIds.length > group.maxSelected) {
          this.modifierErrors[product.id] = `Choose no more than ${group.maxSelected} options from ${group.name}.`;
          return;
        }

        selectedModifiers.push(...group.modifiers.filter((modifier) => selectedIds.includes(modifier.id)));
      }

      const modifierIds = selectedModifiers.map((modifier) => modifier.id).sort();
      const key = [product.id, ...modifierIds].join(":");
      const existing = this.cart.find((line) => line.key === key);
      if (existing) {
        existing.quantity = Math.min(existing.quantity + 1, 99);
      } else {
        this.cart.push({
          key,
          catalogObjectId: product.id,
          title: product.title,
          image: product.image,
          price: product.price,
          currency: product.currency,
          quantity: 1,
          modifiers: selectedModifiers.map((modifier) => ({
            id: modifier.id,
            name: modifier.name,
            price: modifier.price || 0,
          })),
        });
      }

      this.modifierErrors[product.id] = "";
      this.saveCart();
      this.cartOpen = true;
    },

    lineUnitPrice(line) {
      return line.price + line.modifiers.reduce((total, modifier) => total + modifier.price, 0);
    },

    changeCartQuantity(key, change) {
      const line = this.cart.find((item) => item.key === key);
      if (!line) {
        return;
      }

      line.quantity = Math.max(0, Math.min(99, line.quantity + change));
      if (line.quantity === 0) {
        this.removeCartLine(key);
      } else {
        this.saveCart();
      }
    },

    removeCartLine(key) {
      this.cart = this.cart.filter((line) => line.key !== key);
      this.saveCart();
    },

    loadCart() {
      try {
        const savedCart = JSON.parse(localStorage.getItem("bumblefox-square-cart") || "[]");
        this.cart = Array.isArray(savedCart) ? savedCart : [];
      } catch (error) {
        console.error("Could not load cart:", error);
        this.cart = [];
      }
    },

    saveCart() {
      localStorage.setItem("bumblefox-square-cart", JSON.stringify(this.cart));
      window.dispatchEvent(new Event("bumblefox-cart-updated"));
    },

    async checkout() {
      this.checkoutLoading = true;
      this.checkoutError = "";

      try {
        if (!this.shippingCountry) {
          throw new Error("Choose a shipping country before checkout.");
        }
        if (this.shippingCountry === "GB" && !this.normalizedShippingPostcode) {
          throw new Error("Enter a UK postcode before checkout.");
        }
        if (this.shippingCountry === "GB" && !this.validUkShippingPostcode) {
          throw new Error("Enter a valid UK postcode before checkout.");
        }

        const response = await fetch("/api/checkout", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({
            items: this.cart.map((line) => ({
              catalogObjectId: line.catalogObjectId,
              quantity: line.quantity,
              modifierIds: line.modifiers.map((modifier) => modifier.id),
            })),
            shipping: {
              country: this.shippingCountry,
              postalCode: this.shippingPostcode,
            },
          }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body.checkoutUrl) {
          throw new Error(body.error || "Could not open Square checkout.");
        }

        window.location.assign(body.checkoutUrl);
      } catch (error) {
        console.error("Square checkout failed:", error);
        this.checkoutError = error?.message || "Could not open Square checkout.";
        this.checkoutLoading = false;
      }
    },

    formatPrice(amount, currency = "GBP") {
      try {
        return new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency,
        }).format(amount);
      } catch {
        // Fallback if currency/locale formatting fails
        return `${currency} ${Number(amount).toFixed(2)}`;
      }
    },
    determineLogo(brand = "") {
      const normalizedBrand = brand.toLowerCase();
      if(normalizedBrand.includes('cinderwing')){
        return 'cinderwings.png';
      } else if(normalizedBrand.includes('mysticsaige')){
        return 'mystic-sage.png';
      } else if(normalizedBrand.includes('kirsten m')){
        return 'kekreations.png'
      } else if(normalizedBrand.includes('akshan')){
        return 'akshan.png'
      } else if(normalizedBrand.includes('pepunki')){
        return 'pepunki.png'
      } else if(normalizedBrand.includes('bloblab')){
        return 'bloblab.png'
      } else if(normalizedBrand.includes('golden kaeru')){
        return 'golden_kaeru.png'
      } else if(normalizedBrand.includes('kraken designs')){
        return 'kraken-designs.png'
      } else {
        return 'bumblefox-logo.png'
      }
    },
  },
}
</script>

<style>
/* Base styling */
html,
body {
  height: 100%;
  margin: 0;
  color: #2c3e50;
}


header {
  background-color: rgb(255, 214, 116);
  padding: 20px;
}


h1 {
  color: #2e2d61;
  margin-bottom: 10px;
}


.menu-details h3 {
  margin: 10px 0 5px;
}

.menu-details p {
  margin: 5px 0;
}

.menu-note a {
  color: #e86a92;
  text-decoration: none;
}

.menu-note a:hover {
  text-decoration: underline;
}

.shop-logo-overlay {
  position: absolute;
  border-radius: 50%;
  bottom: 6px;
  right: 6px;
  width: 60px;
  height: auto;
  opacity: 0.9;
  pointer-events: none; /* prevents blocking click */
}

footer {
  background-color: #efb26e;
  padding: 10px;
  color: #6c757d;
  font-size: 14px;
}

/* ===== Etsy shop section ===== */
.shop-section {
  background-color: #fff3e4; /* keeps your “section color-coded” vibe */
  padding: 20px;
}

.shop-heading {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.cart-button,
.add-to-cart-button,
.checkout-button {
  min-height: 40px;
  border: 0;
  border-radius: 8px;
  background: #2e2d61;
  color: #fff;
  padding: 9px 14px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.checkout-success {
  max-width: 1100px;
  margin: 10px auto;
  border: 1px solid #8cc89b;
  border-radius: 8px;
  background: #f0fff3;
  color: #225c30;
  padding: 12px;
}

.shop-subtitle {
  margin-top: 6px;
  font-size: 14px;
  color: #555;
}

.shop-updated {
  display: inline-block;
  margin-left: 8px;
  color: #6c757d;
}

.shop-loading {
  padding: 20px;
  color: #555;
}

.shop-controls {
  max-width: 1100px;
  margin: 18px auto 12px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 12px;
}

.shop-search {
  flex: 1;
}

.shop-search input,
.page-size-control select {
  width: 100%;
  min-height: 42px;
  border: 1px solid #c9c9c9;
  border-radius: 8px;
  background: #fff;
  padding: 8px 12px;
  font: inherit;
}

.page-size-control {
  width: 170px;
  display: grid;
  gap: 4px;
  font-size: 13px;
  color: #555;
}

.shop-groups {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 0 8px;
}

.filter-button,
.shop-pagination button {
  min-height: 38px;
  border: 1px solid #c9c9c9;
  border-radius: 8px;
  background: #fff;
  padding: 8px 12px;
  font: inherit;
  cursor: pointer;
  white-space: nowrap;
}

.filter-button.active {
  border-color: #2e2d61;
  background: #2e2d61;
  color: #fff;
}

.shop-results,
.shop-empty {
  max-width: 1100px;
  margin: 8px auto;
  color: #555;
}

.shop-error {
  padding: 14px;
  max-width: 900px;
  margin: 10px auto 0;
  border: 1px solid #f3c6c6;
  background: #fff7f7;
  border-radius: 10px;
  color: #8a2f2f;
  text-align: left;
}

.shop-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.shop-card {
  border: 1px solid #eee;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  text-align: left;
}

.shop-image-wrap {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1/1;
  background: #f6f6f6;
  z-index: 1;
  overflow: hidden;
}

.shop-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shop-image-placeholder {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  color: #fff;
  font-size: 17px;
  font-weight: 800;
  text-align: center;
}

.shop-fallback-image {
  position: absolute;
  inset: 0;
  filter: grayscale(1) contrast(0.72) brightness(0.72);
  transform: scale(1.02);
}

.shop-image-placeholder::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(46, 45, 97, 0.2);
}

.shop-image-placeholder span {
  position: relative;
  z-index: 1;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(31, 30, 54, 0.72);
}

.shop-card-body {
  padding: 12px;
  display: grid;
  gap: 10px;
}

.shop-title {
  color: #2c3e50;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
  min-height: 34px; /* keeps cards tidy */
  text-decoration: none;
}

.shop-title:hover {
  text-decoration: underline;
}

.shop-category {
  color: #6c757d;
  font-size: 12px;
}

.product-options {
  display: grid;
  gap: 10px;
}

.product-option-group {
  display: grid;
  gap: 5px;
}

.product-option-label {
  font-size: 12px;
  font-weight: 700;
}

.product-option-group select {
  width: 100%;
  min-height: 38px;
  border: 1px solid #c9c9c9;
  border-radius: 6px;
  background: #fff;
  padding: 7px;
}

.modifier-checkbox {
  display: flex;
  align-items: start;
  gap: 7px;
  font-size: 13px;
}

.product-option-error,
.cart-error {
  margin: 0;
  color: #8a2f2f;
  font-size: 13px;
}

.shop-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.shop-price {
  font-weight: 700;
  color: #2c3e50;
}

.add-to-cart-button:disabled,
.checkout-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.cart-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.45);
}

.cart-panel {
  width: min(460px, 100%);
  height: 100%;
  overflow-y: auto;
  background: #fff;
  padding: 20px;
  box-sizing: border-box;
  text-align: left;
}

.cart-header,
.cart-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cart-header h2 {
  margin: 0;
}

.cart-close {
  border: 0;
  background: transparent;
  font-size: 30px;
  cursor: pointer;
}

.cart-empty {
  color: #555;
}

.cart-lines {
  margin-top: 18px;
  display: grid;
  gap: 14px;
}

.cart-line {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
  border-bottom: 1px solid #e1e1e1;
  padding-bottom: 14px;
}

.cart-line img {
  width: 64px;
  height: 64px;
  border-radius: 6px;
  object-fit: cover;
}

.cart-line-details {
  display: grid;
  gap: 3px;
  font-size: 13px;
}

.cart-quantity {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cart-quantity button {
  width: 30px;
  height: 30px;
  border: 1px solid #c9c9c9;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.cart-remove {
  grid-column: 2 / -1;
  justify-self: start;
  border: 0;
  background: transparent;
  color: #8a2f2f;
  padding: 0;
  cursor: pointer;
}

.cart-footer {
  margin-top: 22px;
  display: grid;
  gap: 12px;
}

.shipping-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(120px, 0.7fr);
  gap: 10px;
}

.shipping-fields label {
  display: grid;
  gap: 5px;
  color: #444;
  font-size: 13px;
  font-weight: 700;
}

.shipping-fields select,
.shipping-fields input {
  width: 100%;
  min-height: 42px;
  border: 1px solid #c9c9c9;
  border-radius: 6px;
  background: #fff;
  padding: 8px 10px;
  box-sizing: border-box;
  color: #2c3e50;
}

.shipping-summary {
  margin: 0;
  color: #555;
  font-size: 13px;
}

.cart-total {
  font-size: 18px;
}

.cart-note {
  margin: 0;
  color: #666;
  font-size: 12px;
}

.checkout-button {
  width: 100%;
  min-height: 48px;
}

.shop-pagination {
  max-width: 1100px;
  margin: 20px auto 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
}

.shop-pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

@media (max-width: 640px) {
  .shop-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .page-size-control {
    width: 100%;
  }

  .shipping-fields {
    grid-template-columns: 1fr;
  }

  .shop-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .shop-card-body {
    padding: 10px;
  }

  .shop-title {
    font-size: 0.95rem;
  }
}

.shop-btn {
  display: inline-block;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #111;
  text-decoration: none;
  color: #111;
  font-weight: 700;
  font-size: 13px;
}
</style>
